import React, { useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import {
  Bounds,
  Grid,
  OrbitControls,
  GizmoHelper,
  GizmoViewport,
  Html,
  useGLTF,
} from "@react-three/drei";
import * as THREE from "three";
import DrillingPreviewMesh from "./DrillingPreviewMesh";
import DimensionRenderer3D from "../dimensions/DimensionRenderer3D";
import GeneratedAssetMesh from "../asset-geometry/GeneratedAssetMesh";


function degToRad(value) {
  return (Number(value) || 0) * Math.PI / 180;
}

function vec3(value = {}) {
  return [
    Number(value.x) || 0,
    Number(value.y) || 0,
    Number(value.z) || 0,
  ];
}

function rot3(value = {}) {
  return [
    degToRad(value.x),
    degToRad(value.y),
    degToRad(value.z),
  ];
}

function WorkPlaneMesh({ workPlane }) {
  const plane = workPlane?.plane || "xy";
  const offset = Number(workPlane?.offset || 0);

  const size = 500;

  let rotation = [0, 0, 0];
  let position = [0, 0, 0];

  if (plane === "xy") {
    rotation = [0, 0, 0];
    position = [0, 0, offset];
  }

  if (plane === "xz") {
    rotation = [-Math.PI / 2, 0, 0];
    position = [0, offset, 0];
  }

  if (plane === "yz") {
    rotation = [0, Math.PI / 2, 0];
    position = [offset, 0, 0];
  }

  return (
    <mesh position={position} rotation={rotation}>
      <planeGeometry args={[size, size]} />
      <meshStandardMaterial
        color="#60a5fa"
        transparent
        opacity={0.16}
        side={THREE.DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
}

function MeasurePlacementPlane({
  toolMode,
  pickedPoints,
  workPlane,
  onPickPoint,
}) {
  if (toolMode !== "measure") return null;
  if (!Array.isArray(pickedPoints) || pickedPoints.length !== 2) return null;

  const plane = workPlane?.plane || "xy";
  const offset = Number(workPlane?.offset || 0);
  const size = 5000;

  let rotation = [0, 0, 0];
  let position = [0, 0, 0];

  if (plane === "xy") {
    rotation = [0, 0, 0];
    position = [0, 0, offset];
  }

  if (plane === "xz") {
    rotation = [-Math.PI / 2, 0, 0];
    position = [0, offset, 0];
  }

  if (plane === "yz") {
    rotation = [0, Math.PI / 2, 0];
    position = [offset, 0, 0];
  }

  return (
    <mesh
      position={position}
      rotation={rotation}
      renderOrder={5}
      onPointerDown={(e) => {
        e.stopPropagation();

        const worldPoint = e.point.clone();

        onPickPoint?.({
          type: "dimension_placement",
          world: {
            x: Number(worldPoint.x.toFixed(3)),
            y: Number(worldPoint.y.toFixed(3)),
            z: Number(worldPoint.z.toFixed(3)),
          },
          local: {
            x: Number(worldPoint.x.toFixed(3)),
            y: Number(worldPoint.y.toFixed(3)),
            z: Number(worldPoint.z.toFixed(3)),
          },
        });
      }}
    >
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial
        transparent
        opacity={0}
        depthWrite={false}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

function Marker({ position = [0, 0, 0], color = "red", label }) {
  return (
    <group position={position}>
      <mesh>
        <sphereGeometry args={[0.5, 20, 20]} />
        <meshStandardMaterial color={color} />
      </mesh>

      {label ? (
        <Html distanceFactor={12}>
          <div className="px-2 py-1 rounded bg-white border text-xs whitespace-nowrap">
            {label}
          </div>
        </Html>
      ) : null}
    </group>
  );
}

function BoardPlane({ width = 400, height = 300 }) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[width / 2, 0, height / 2]}>
      <planeGeometry args={[width, height]} />
      <meshStandardMaterial
        color="#e5e7eb"
        side={THREE.DoubleSide}
        transparent
        opacity={0.35}
      />
    </mesh>
  );
}

function GlbModel({
  url,
  onError,
  modelOffset = { x: 0, y: 0, z: 0 },
  modelRotation = { x: 0, y: 0, z: 0 },
  toolMode = "select",
  pickedPoints = [],
  onPickPoint,
}) {
  const groupRef = useRef(null);

  const { scene } = useGLTF(url, undefined, undefined, (err) => {
    console.error("GLB load error:", err);
    onError?.(err);
  });

  const cloned = useMemo(() => {
  const next = scene.clone(true);

  // VAŽNO:
  // Ne centriramo GLB.
  // Asset mora ostati u stvarnom lokalnom koordinatnom sistemu iz STEP/GLB fajla.
  next.position.set(0, 0, 0);
  next.rotation.set(0, 0, 0);
  next.scale.set(1, 1, 1);

  next.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });

  return next;
}, [scene]);

  return (
    <group
      ref={groupRef}
      position={vec3(modelOffset)}
      rotation={rot3(modelRotation)}
      onPointerDown={(e) => {
  if (toolMode !== "pick_point" && toolMode !== "measure") return;

  // U measure modu GLB služi samo za prvu i drugu tačku.
  // Treća tačka ide preko MeasurePlacementPlane u prostoru.
  if (
    toolMode === "measure" &&
    Array.isArray(pickedPoints) &&
    pickedPoints.length >= 2
  ) {
    return;
  }

  e.stopPropagation();

  const worldPoint = e.point.clone();

  const localPoint = groupRef.current
    ? groupRef.current.worldToLocal(worldPoint.clone())
    : worldPoint.clone();

  onPickPoint?.({
    type: "glb_point",
    world: {
      x: Number(worldPoint.x.toFixed(3)),
      y: Number(worldPoint.y.toFixed(3)),
      z: Number(worldPoint.z.toFixed(3)),
    },
    local: {
      x: Number(localPoint.x.toFixed(3)),
      y: Number(localPoint.y.toFixed(3)),
      z: Number(localPoint.z.toFixed(3)),
    },
  });
}}
    >
      <primitive object={cloned} />
    </group>
  );
}

function MountingItemMarker({ item }) {
  const p = item?.position_json || { x: 0, y: 0, z: 0 };
  const r = item?.rotation_json || { x: 0, y: 0, z: 0 };

  const position = [
    Number(p.x) || 0,
    Number(p.y) || 0,
    Number(p.z) || 0,
  ];

  const rotation = [
    degToRad(r.x),
    degToRad(r.y),
    degToRad(r.z),
  ];

  const asset = item?.asset || null;
  const geometry = asset?.metadata_json?.geometry || null;

  const label =
    item?.item?.code ||
    item?.item?.name ||
    asset?.code ||
    asset?.name ||
    `Item ${item?.item_id || ""}`;

  if (geometry) {
    return (
      <group position={position} rotation={rotation}>
        <GeneratedAssetMesh geometry={geometry} />

        <Html distanceFactor={12}>
          <div className="px-2 py-1 rounded bg-white border text-xs whitespace-nowrap">
            {label} · qty {item.quantity || 1}
          </div>
        </Html>
      </group>
    );
  }

  return (
    <group position={position} rotation={rotation}>
      <mesh>
        <sphereGeometry args={[5, 24, 24]} />
        <meshStandardMaterial color="#f97316" />
      </mesh>

      <Html distanceFactor={12}>
        <div className="px-2 py-1 rounded bg-white border text-xs whitespace-nowrap">
          {label} · qty {item.quantity || 1}
        </div>
      </Html>
    </group>
  );
}

function OriginAxes({ label, color = "#ef4444", size = 80 }) {
  return (
    <group>
      <axesHelper args={[size]} />

      <mesh>
        <sphereGeometry args={[5, 24, 24]} />
        <meshStandardMaterial color={color} />
      </mesh>

      <Html distanceFactor={12}>
        <div className="px-2 py-1 rounded bg-white border text-xs whitespace-nowrap shadow">
          {label}
        </div>
      </Html>
    </group>
  );
}

export default function MountingPreview3D({
  mountingModel,
  glbUrl = "",
  generatedGeometry = null,
  previewWidth = 600,
  previewHeight = 400,
  selectedDrillId = null,
  onSelectDrill,
  toolMode = "select",
  pickedPoints = [],
  savedDimensions = [],
  selectedDimensionId = null,
  onSelectDimension,
  dimensionMode = "free",
  markerSize = 0.5,
  dimensionOffset = 10,
  onPickPoint,
  mountingItems = [],
  showOrigins = true,
}) {
  const controlsRef = useRef(null);
  const safe = mountingModel || {
    origin: { x: 0, y: 0, z: 0 },
    attachPoints: [],
    holes: [],
    cutouts: [],
  };

  const attachPoints = safe.attachPoints || [];
const holes = safe.holes || [];
const cutouts = safe.cutouts || [];
const drillings = Array.isArray(safe.drillings) ? safe.drillings : [];

const modelOffset = safe.offset || { x: 0, y: 0, z: 0 };
const modelRotation = safe.defaultRotation || { x: 0, y: 0, z: 0 };

  const content = useMemo(() => {
    return (
      <group>
  <BoardPlane width={400} height={300} />

  {showOrigins ? (
    <OriginAxes
      label="MOUNTING ORIGIN 0,0,0"
      color="#ef4444"
      size={120}
    />
  ) : null}

  {/* GLB se pomera u odnosu na origin */}
  {glbUrl ? (
  <GlbModel
    url={glbUrl}
    modelOffset={modelOffset}
    modelRotation={modelRotation}
    toolMode={toolMode}
    pickedPoints={pickedPoints}
    onPickPoint={onPickPoint}
  />
) : generatedGeometry ? (
  <group position={vec3(modelOffset)} rotation={rot3(modelRotation)}>
    <GeneratedAssetMesh geometry={generatedGeometry} />
  </group>
) : null}

  {/* Work plane, rupe i šrafovi ostaju u origin sistemu */}
  <WorkPlaneMesh workPlane={safe.workPlane} />

  {drillings
    .filter((drill) => drill?.enabled !== false)
    .map((drill) => (
      <DrillingPreviewMesh
        key={drill.id}
        drill={drill}
        selected={String(selectedDrillId) === String(drill.id)}
        onClick={() => onSelectDrill?.(drill.id)}
      />
    ))}

  {mountingItems
    .filter((item) => item?.status !== "deleted")
    .map((item) => (
      <MountingItemMarker key={item.id} item={item} />
    ))}

<MeasurePlacementPlane
  toolMode={toolMode}
  pickedPoints={pickedPoints}
  workPlane={safe.workPlane}
  onPickPoint={onPickPoint}
/>

<DimensionRenderer3D
  pickedPoints={pickedPoints}
  savedDimensions={savedDimensions}
  selectedDimensionId={selectedDimensionId}
  dimensionMode={dimensionMode}
  markerSize={markerSize}
  dimensionOffset={dimensionOffset}
  workPlane={safe.workPlane}
  onSelectDimension={onSelectDimension}
/>

        {attachPoints.map((item) => (
          <Marker
            key={item.id}
            position={[
              Number(item.x) || 0,
              Number(item.z) || 0,
              Number(item.y) || 0,
            ]}
            color="#2563eb"
            label={item.name || "Attach"}
          />
        ))}

        {holes.map((item) => (
          <Marker
            key={item.id}
            position={[
              Number(item.x) || 0,
              Number(item.z) || 0,
              Number(item.y) || 0,
            ]}
            color="#16a34a"
            label={item.name || `Hole ${item.diameter || 0}`}
          />
        ))}

        {cutouts.map((item) => (
          <Marker
            key={item.id}
            position={[
              Number(item.x) || 0,
              Number(item.z) || 0,
              Number(item.y) || 0,
            ]}
            color="#7c3aed"
            label={item.name || `Cutout ${item.diameter || 0}`}
          />
        ))}
      </group>
    );
 }, [
  safe,
  attachPoints,
  holes,
  cutouts,
  drillings,
  glbUrl,
  modelOffset,
  modelRotation,
  selectedDrillId,
  onSelectDrill,
  toolMode,
  pickedPoints,
  dimensionMode,
  markerSize,
  dimensionOffset,
  savedDimensions,
selectedDimensionId,
onSelectDimension,
  onPickPoint,
  mountingItems,
]);

  return (
    <div
      className="w-full h-full border rounded overflow-hidden"
      style={{ minHeight: previewHeight }}
    >
      <Canvas camera={{ position: [500, 400, 500], fov: 28, near: 1, far: 20000 }}>
        <color attach="background" args={["#f5f5f5"]} />
        <ambientLight intensity={1.5} />
        <directionalLight position={[1000, 1500, 1200]} intensity={1.2} />

        <Grid
          args={[5000, 5000]}
          cellSize={50}
          sectionSize={250}
          fadeDistance={10000}
          fadeStrength={1}
          infiniteGrid
        />

        <Bounds fit clip observe margin={1.3}>
          {content}
        </Bounds>

        <OrbitControls
  ref={controlsRef}
  makeDefault
  enableDamping={false}
  dampingFactor={0}
  rotateSpeed={0.65}
  zoomSpeed={0.8}
  panSpeed={0.8}
/>
        <GizmoHelper alignment="bottom-right" margin={[80, 80]}>
          <GizmoViewport />
        </GizmoHelper>
      </Canvas>
    </div>
  );
}